// What a run CONCLUDED, as one pill.
//
// Separate from StatusBadge, which says where a run is in its lifecycle. The
// two answer different questions and a reader needs both: a run can be
// `completed` and malicious, or `completed` and clean, and collapsing them into
// one column loses whichever half is not shown.
//
// A run with no verdict yet renders as a dash rather than as a word. There is
// no "unknown" pill on purpose - a queued or running submission has not been
// judged, and every word available for that state ("clean", "safe", "none")
// reads as a finding.

import { cn } from '@/lib/classnames';
import type { RunTag } from '@/lib/api-types.gen';

/** The verdicts the aggregator writes, in the palette each has earned. */
const VERDICT_TONE: Record<string, string> = {
  malicious: 'border-verdict-malicious/40 bg-verdict-malicious/[0.10] text-verdict-malicious',
  suspicious: 'border-verdict-suspicious/40 bg-verdict-suspicious/[0.10] text-verdict-suspicious',
  clean: 'border-verdict-clean/40 bg-verdict-clean/[0.10] text-verdict-clean',
  benign: 'border-verdict-clean/40 bg-verdict-clean/[0.10] text-verdict-clean',
  inconclusive: 'border-ink-700 text-ink-300',
  bypassed: 'border-cyan-500/40 bg-cyan-500/[0.10] text-cyan-300',
};

export function VerdictBadge({
  verdict,
  family,
  tags,
  originalVerdict,
}: {
  verdict?: string | null;
  family?: string | null;
  /** What the run CONTAINED, beside what it concluded. See RunTag. */
  tags?: readonly RunTag[] | null;
  /** Prior platform verdict if this run had an alarming verdict overridden */
  originalVerdict?: string | null;
}) {
  const key = (verdict ?? '').trim().toLowerCase();
  const namedFamily = family?.trim();
  const hasNamedFamily =
    namedFamily !== undefined &&
    namedFamily.length > 0 &&
    !/^no family\b/i.test(namedFamily);
  // A run with no verdict can still carry tags - a stopped page that was
  // flagged explicit before it was judged - so the marks are drawn either way
  // rather than behind the verdict's own early return.
  if (key === '') {
    return tags && tags.length > 0 ? (
      <span className="inline-flex items-center gap-2 min-w-0">
        <RunTags tags={tags} />
      </span>
    ) : (
      <span className="text-muted">-</span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <span className={cn('pill', VERDICT_TONE[key] ?? 'border-ink-700 text-ink-300')}>
        {key.charAt(0).toUpperCase() + key.slice(1)}
      </span>
      {key === 'bypassed' && originalVerdict && (
        <span
          className="pill border-verdict-malicious/40 bg-verdict-malicious/[0.10] text-verdict-malicious max-w-[10rem]"
          title={`Platform originally classified as ${originalVerdict}; overridden by operator approval`}
        >
          <span className="truncate">Prior: {originalVerdict}</span>
        </span>
      )}
      {/* The family is a second tag, and deliberately NOT in the verdict
          palette. It names WHAT the thing is, which is an identity rather than
          a severity - a red "Formbook" beside an amber "Suspicious" would be
          two different severities for one row. The accent is the same one the
          intel panel uses for a record: identified, not judged. */}
      {hasNamedFamily && (
        <span
          className="pill border-accent-500/40 bg-accent-500/[0.10] text-accent-200 max-w-[10rem]"
          title={namedFamily}
        >
          <span className="truncate">{namedFamily}</span>
        </span>
      )}
      <RunTags tags={tags} />
    </span>
  );
}

/**
 * The marks a run's analyzer wants carried beside its verdict.
 *
 * In the NEUTRAL palette, deliberately, and never in the verdict one: these
 * say what the run CONTAINED, not what it concluded. A page can be clean and
 * still carry explicit imagery or a QR destination, and colouring "Explicit"
 * red would make a second severity out of a fact - which is the same rule the
 * family pill above follows for the same reason.
 */
function RunTags({ tags }: { tags?: readonly RunTag[] | null }) {
  if (!tags || tags.length === 0) return null;
  return (
    <>
      {tags.map((t) => (
        <span
          key={t.label}
          className="pill border-ink-700 text-ink-300 max-w-[10rem]"
          title={t.title || t.label}
        >
          <span className="truncate">{t.label}</span>
        </span>
      ))}
    </>
  );
}
